import React, { useEffect, useState } from 'react';

import { observer } from 'mobx-react';
import { createUseStyles } from 'react-jss';

import { useStores } from 'hooks/use-stores';
import useHoverPayoutText from 'react_pages/HomeNew/components/useHoverPayoutText';
import { BinaryMarket } from 'stores/domain/BinaryMarket';
import { OrderAction, OrderSide } from 'stores/ui/orderPanel/orderPanel';
import { asCents } from 'utils/caseing';
import { isValidPrice } from 'utils/price';

import { NumberChangeAnimationWrapper } from '../Animation';
import Pill from '../Pill/Pill';
import Text from '../Text';
import { BasePillProps } from './Pill';

const useStyles = createUseStyles({
    textContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        width: '100%',
        height: '100%',
    },
    pillText: {
        position: 'absolute',
        transition: 'opacity 150ms ease-in-out',
        opacity: 1,
    },
    priceText: {
        position: 'absolute',
        transition: 'opacity 150ms ease-in-out',
        opacity: 0,
    },
    hovered: {
        '& $pillText': {
            opacity: 0,
        },
        '& $priceText': {
            opacity: 1,
        },
    },
});

interface PricePillProps extends Omit<BasePillProps, 'variant'> {
    action: OrderAction;
    side: OrderSide;
    market: BinaryMarket;
    compact?: boolean;
    price?: number;
    animate?: boolean;
    externalTrigger?: boolean;
    width?: number | string;
    variant?: 'marketTile' | 'default' | 'portfolio';
    dontLoadData?: boolean;
    className?: string;
}

const PricePill = ({
    action,
    side,
    market,
    compact = false,
    price = 0,
    externalTrigger = false,
    animate = true,
    width = 104,
    variant = 'default',
    dontLoadData,
    className,
    ...commonPillProps
}: PricePillProps): JSX.Element => {
    const classes = useStyles();

    const { traderStore } = useStores();

    const [isHovering, setIsHovering] = useState(false);
    const [lastPrice, setLastPrice] = useState<number | undefined>(price);
    const [priceChanged, setPriceChanged] = useState(false);

    // Update priceChanged state when price changes
    useEffect(() => {
        if (price !== lastPrice) {
            setPriceChanged(true);
            setLastPrice(price);

            setTimeout(() => {
                setPriceChanged(false);
            }, 3000);
        }
    }, [price]);

    const event = market.parentEvent;
    const series = event.series;

    const { yesPayout, noPayout, isPayoutLoaded } = useHoverPayoutText({
        eventTicker: event.ticker,
        marketTicker: market.ticker,
        forceLoad: !dontLoadData,
        forceUpdate: priceChanged,
        dontDispose: true,
    });

    const PillTextComponent = ['marketTile', 'portfolio'].includes(variant) ? Text.Emphasis.x20 : Text.Emphasis.x30;

    const isYes = side === 'yes';
    const isSell = action === 'sell';
    const pillText = <PillTextComponent>{action === 'buy' ? (isYes ? 'Yes' : 'No') : 'Sell'}</PillTextComponent>;

    const useAmericanOdds =
        traderStore.user.properties.americanOdds && series?.usesAmericanOdd && !isSell && !!market.lastPrice;
    const priceString = isValidPrice(price) ? asCents(price) : variant === 'marketTile' ? pillText : '';
    const payout = isPayoutLoaded ? (isYes ? yesPayout : noPayout) : null; // null payout means loading
    const oddsString = useAmericanOdds ? market.americanOdds(isYes, isSell, payout) : priceString;

    const priceText =
        variant === 'marketTile' ? (
            <Text.Emphasis.x20>{oddsString}</Text.Emphasis.x20>
        ) : (useAmericanOdds && compact) || variant === 'portfolio' ? (
            <Text.Emphasis.x30>{oddsString}</Text.Emphasis.x30>
        ) : (
            <Text.Emphasis.x40>{oddsString}</Text.Emphasis.x40>
        );

    const { buttonProps, ...otherCommonPillProps } = commonPillProps;

    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);
    const hoverProps = { onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave };

    let shownText;
    if (variant === 'marketTile') {
        shownText = (
            <div className={`${classes.textContainer} ${isHovering ? classes.hovered : ''}`}>
                <div className={classes.pillText}>{pillText}</div>
                <div className={classes.priceText}>
                    <NumberChangeAnimationWrapper
                        number={price}
                        externalTrigger={externalTrigger}
                        scrollAnimate={false}
                        animate={animate}
                    >
                        {priceText}
                    </NumberChangeAnimationWrapper>
                </div>
            </div>
        ) as unknown as string;
    } else {
        shownText = (
            <NumberChangeAnimationWrapper
                number={price}
                externalTrigger={externalTrigger}
                scrollAnimate={false}
                animate={animate}
            >
                {pillText}&nbsp; {priceText}
            </NumberChangeAnimationWrapper>
        ) as unknown as string;
    }

    return (
        <Pill
            text={shownText}
            variant={action === 'sell' ? 'sell' : side}
            dataTestId={'price-pill'}
            buttonProps={{ style: { width: width, ...buttonProps?.style }, ...buttonProps, ...hoverProps }}
            className={className}
            {...otherCommonPillProps}
        />
    );
};

export default observer(PricePill);
